import { useTranslation } from "react-i18next"
import { Box, Stack, Typography } from "@mui/material"
import WarningIcon from "@mui/icons-material/WarningRounded"

import type { OpenAlertsSummary } from "types/alerts.types"

import { ALERTS_SUMMARY_ENTITIES } from "helpers/utils/alerts"
import { SectionCard } from "widgets/styled/containers"
import AlertsOverviewLink from "./AlertsOverviewLink"
import EntityStat from "./EntityStat"
import LastUpdated from "./LastUpdated"
import { CELL_BORDERS } from "./styles"

interface OpenAlertsCardProps {
  summary: OpenAlertsSummary
  lastUpdatedAt?: number
}

const OpenAlertsCard = ({ summary, lastUpdatedAt }: OpenAlertsCardProps) => {
  const { t } = useTranslation()

  return (
    <SectionCard
      data-testid="open-alerts-card"
      sx={{
        my: 0,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.6fr 3fr" },
        containerType: "inline-size",
      }}
    >
      <Box
        sx={{
          ...CELL_BORDERS,
          borderBottomWidth: { xs: 1, md: 0 },
          p: { xs: 2, md: 2.5 },
          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          columnGap={2}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <WarningIcon color="error" sx={{ fontSize: 14 }} />
            <Typography variant="overlineDense">
              {t("alerts_summary.OPEN_ALERTS")}
            </Typography>
          </Stack>
          <AlertsOverviewLink />
        </Stack>
        <Typography
          sx={{
            fontSize: { xs: "2rem", md: "2.25rem" },
            fontWeight: "fontWeightBold",
            lineHeight: 1,
            mt: 1,
          }}
        >
          {summary.openAlerts}
        </Typography>
        <Typography variant="caption" color="neutral.600" component="p" mt={1}>
          {t("alerts_summary.AFFECTED_ENTITIES", { count: summary.affectedEntities })}
        </Typography>
        <LastUpdated at={lastUpdatedAt} mt={0.5} />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          // The last row takes the leftover height, so the dividers reach the bottom
          gridTemplateRows: "auto auto auto 1fr",
        }}
      >
        {ALERTS_SUMMARY_ENTITIES.map((entity, index) => (
          <EntityStat
            key={entity}
            entity={entity}
            affected={summary.affectedByEntity[entity]}
            isFirst={index === 0}
          />
        ))}
      </Box>
    </SectionCard>
  )
}

export default OpenAlertsCard
